import { pushDataLayerEvent } from "@/helpers/googleTagManager"
import { useEffect, useRef } from "react"
import ReactGA from "react-ga4"
import { useLocation } from "react-router-dom"

let isInitialized = false

/**
 * Validate Google Analytics 4 measurement ID format
 * GA4 IDs should start with 'G-' (e.g., G-XXXXXXXXXX)
 * AW- IDs are for Google Ads conversion tracking, not GA4
 */
const isValidGA4Id = (id: string): boolean => {
  if (!id || id === "0000000" || id === "") {
    return false
  }

  // GA4 measurement IDs start with 'G-'
  if (id.startsWith("G-")) {
    return true
  }

  // Warn about common mistakes
  if (id.startsWith("AW-")) {
    console.error(
      'Google Analytics: Invalid ID format. "AW-" is a Google Ads conversion ID, not a GA4 measurement ID.\n' +
        "Please use your Google Analytics 4 Measurement ID (format: G-XXXXXXXXXX).\n" +
        "Find it in: Google Analytics > Admin > Data Streams > [Your Stream] > Measurement ID"
    )
    return false
  }

  if (id.startsWith("UA-")) {
    console.error(
      "Google Analytics: Universal Analytics (UA-) is deprecated. Please use a GA4 Measurement ID (format: G-XXXXXXXXXX)."
    )
    return false
  }

  console.warn(
    `Google Analytics: ID format "${id}" doesn't match expected GA4 format (G-XXXXXXXXXX). ` +
      "This may not work correctly."
  )
  return false
}

/**
 * Initialize Google Analytics 4
 * Should only be called once when the app loads
 */
export const initializeGA = (measurementId: string): boolean => {
  // Validate the measurement ID format
  if (!isValidGA4Id(measurementId)) {
    if (import.meta.env.DEV) {
      console.warn("Google Analytics: Invalid or missing measurement ID")
    }
    return false
  }

  // Prevent multiple initializations
  if (isInitialized) {
    return true
  }

  try {
    ReactGA.initialize(measurementId, {
      testMode: import.meta.env.DEV,
    })
    isInitialized = true

    if (import.meta.env.DEV) {
      console.log("Google Analytics: Initialized with ID", measurementId)
    }
    return true
  } catch (error) {
    console.error("Google Analytics: Initialization error", error)
    return false
  }
}

/**
 * Hook to track page views on route changes
 * Should be used in the main App component or Router component
 *
 * @param options - Configuration options
 * @param options.excludePaths - Array of path prefixes to exclude from tracking (e.g., ['/dashboard'])
 */
export const useGoogleAnalytics = (options?: { excludePaths?: string[] }) => {
  const location = useLocation()
  const measurementId = import.meta.env.WEBSITE_GOOGLE_ANALYTICS_ID || ""
  const prevPathRef = useRef<string>("")
  const excludePaths = options?.excludePaths || []

  // Check if current path should be excluded
  const shouldExcludePath = (path: string): boolean => {
    return excludePaths.some(excludedPath => path.startsWith(excludedPath))
  }

  useEffect(() => {
    // Initialize GA on mount if not already initialized
    // Validation happens inside initializeGA
    if (measurementId) {
      initializeGA(measurementId)
    }
  }, [measurementId])

  useEffect(() => {
    // Track pageview on route change
    if (isInitialized && isValidGA4Id(measurementId)) {
      const currentPath = location.pathname + location.search

      // Skip tracking if path is excluded
      if (shouldExcludePath(location.pathname)) {
        if (import.meta.env.DEV) {
          console.log("GA Pageview (excluded):", currentPath)
        }
        return
      }

      // Only track if path actually changed
      if (prevPathRef.current !== currentPath) {
        prevPathRef.current = currentPath

        // Use the correct GA4 API for pageview tracking
        ReactGA.send({
          hitType: "pageview",
          page: currentPath,
          title: document.title || location.pathname,
        })
      }
    }
  }, [location.pathname, location.search, measurementId, excludePaths])
}

/**
 * A lead: the visitor sent a form that asks Easetech for work, e.g. a quote request. Sent to GA4 as "generate_lead"
 * (GA4's recommended lead event; mark it as a key event in GA4 → Admin → Events) and pushed to the dataLayer, where a
 * GTM trigger can use it (e.g. a Google Ads conversion). The dataLayer keeps the event if GTM has not loaded yet.
 */
export const trackLead = (form: string) => {
  pushDataLayerEvent("generate_lead", { form })
  const measurementId = import.meta.env.WEBSITE_GOOGLE_ANALYTICS_ID || ""
  if (!isInitialized || !isValidGA4Id(measurementId)) return
  try {
    ReactGA.event("generate_lead", { form })
  } catch (error) {
    console.error("Google Analytics: Event tracking error", error)
  }
}

/**
 * Track custom events with GA4
 */
export const trackEvent = (category: string, action: string, label?: string, value?: number) => {
  const measurementId = import.meta.env.WEBSITE_GOOGLE_ANALYTICS_ID || ""

  if (!isInitialized || !isValidGA4Id(measurementId)) {
    if (import.meta.env.DEV) {
      console.log("GA Event (not sent):", { category, action, label, value })
    }
    return
  }

  try {
    ReactGA.event({
      category,
      action,
      label,
      value,
    })

    if (import.meta.env.DEV) {
      console.log("GA Event:", { category, action, label, value })
    }
  } catch (error) {
    console.error("Google Analytics: Event tracking error", error)
  }
}
